"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUserGuideCompletions,
  putUserGuideCompletion,
  USER_GUIDE_COMPLETIONS_GET,
} from "@/api/client";
import { UserGuideId } from "@/constants/user-guides";
import { parseApiError } from "@/lib/errorUtils";
import type { UserGuideCompletion, UserGuideStatus } from "@/types";

/**
 * Seen-state for one-time user guides (FE-1131), stored per user on the
 * monolith (GET/PUT /api/user-guide-completion) so it follows the user
 * across browsers and devices. Fetched once per session; a guide absent
 * from the response has never been seen — COMPLETED and DISMISSED are
 * equivalent for "should I show it?".
 */
export const useUserGuides = () => {
  const queryClient = useQueryClient();

  const { data: completions, isSuccess } = useQuery({
    queryKey: [USER_GUIDE_COMPLETIONS_GET],
    queryFn: getUserGuideCompletions,
    // Seen-state only grows, so never refetch — and never GC the entry
    // when the guide's page unmounts, or every later visit re-fetches.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // COMPLETED and DISMISSED both count as seen — only absence means unseen.
  const hasSeenGuide = useCallback(
    (guideId: UserGuideId) =>
      (completions ?? []).some((completion) => completion.guide === guideId),
    [completions],
  );

  const markGuideSeen = useCallback(
    (guideId: UserGuideId, status: UserGuideStatus) => {
      // Joyride can report the same tour end twice (TARGET_NOT_FOUND is
      // followed by a TOUR_END once the index runs past the last step) —
      // keep the first status.
      const cached = queryClient.getQueryData<UserGuideCompletion[]>([
        USER_GUIDE_COMPLETIONS_GET,
      ]);
      if (cached?.some((completion) => completion.guide === guideId)) return;

      // Optimistically patch the cached completions so the guide can't
      // re-trigger this session, then persist fire-and-forget — if the
      // write fails, the only cost is the guide re-showing on the next
      // visit (accepted failure mode).
      queryClient.setQueryData<UserGuideCompletion[]>(
        [USER_GUIDE_COMPLETIONS_GET],
        (prev) => [
          ...(prev ?? []).filter((completion) => completion.guide !== guideId),
          { guide: guideId, status, updatedAt: Date.now() },
        ],
      );
      putUserGuideCompletion(guideId, status).catch((error) => {
        // Deliberately no toast: the write is a background nicety and the
        // guide is already hidden for this session. Log the subcode — the
        // raw error's message is stripped in production.
        console.error(
          "Failed to persist user-guide seen state:",
          parseApiError(error).subcode,
        );
      });
    },
    [queryClient],
  );

  // isReady gates rendering: until the fetch succeeds we don't know what
  // the user has seen, so guides must not flash for returning users.
  return { hasSeenGuide, markGuideSeen, isReady: isSuccess };
};
